import { defineStore } from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { useNotify } from '@/app/providers/notify';
import { useLoyaltyApi } from '@/plugins/api/loyalty';
import { useSentry } from '@/plugins/sentry';
import { useAuthStore } from '@/store/authStore';
import type {
  AcceptRewardRequest,
  Advantage,
  Agent,
  Condition,
  ConditionItem,
  RewardRequest,
  RewardResponse,
  StatusLoyalty,
} from '@/types/Loyalty';

import {
  EMPTY_AGENT_INFO,
  mapLoyaltyLevelStatus,
  mapLoyaltyPrivileges,
} from './lib';

import type {
  AgentResponse,
  LoyaltyLevelStatus,
  LoyaltyPrivilege,
  OfferResponse,
  PointsHistoryResponse,
} from './types';

export const useLoyaltyStore = defineStore('loyalty', () => {
  const agentInfo = ref<AgentResponse>();
  const offer = ref<OfferResponse>();
  const advantages = ref<Array<Advantage>>();
  const pointsHistory = ref<Array<PointsHistoryResponse>>();
  const conditions = ref<Condition>({});
  const conditionsLoading = ref(false);
  const conditionsError = ref(false);
  const rewardCards = ref<Array<RewardResponse>>();

  const agent = computed<Agent | undefined>(() => agentInfo.value?.agent);

  const loyaltyStatus = computed<StatusLoyalty | undefined>(() => agentInfo.value?.status);

  const loyaltyLevelStatus = computed<LoyaltyLevelStatus>(() => mapLoyaltyLevelStatus(loyaltyStatus.value));

  const privileges = computed<Array<LoyaltyPrivilege> | undefined>(() => mapLoyaltyPrivileges(agent.value?.privileges));

  const isLastLevel = computed<boolean>(() => loyaltyLevelStatus.value.nextAmount === null);

  const isShowIcon = computed<boolean>(() => Boolean(loyaltyLevelStatus.value.icon));

  function getUserToken () {
    return useAuthStore().getUserToken();
  }

  async function getAgentInfo (): Promise<void> {
    try {
      const url = '/v1/agent/info';
      const token = getUserToken();
      const {
        data,
      } = await useLoyaltyApi().get<AgentResponse>(url, {
        headers: {
          Authorization: token,
        },
      });
      agentInfo.value = data;
    } catch (error: any) {
      if (error.response.status === 400) {
        agentInfo.value = EMPTY_AGENT_INFO;
      } else {
        console.error('🚀 ~ file: loyalty.ts ~ getAgentInfo ~ error', error);
        useSentry().captureException(error);
      }
    }
  }

  async function getOffer (): Promise<void> {
    try {
      const url = '/v1/content/offer';
      const {
        data,
      } = await useLoyaltyApi().get<OfferResponse>(url);

      offer.value = data;
    } catch (error) {
      console.error('🚀 ~ file: loyalty.ts ~ getOffer ~ error', error);
      useSentry().captureException(error);
    }
  }

  async function acceptOffer (): Promise<void> {
    try {
      const url = 'v1/agent/accept_offer';
      const token = getUserToken();
      await useLoyaltyApi().post(url, {}, {
        headers: {
          Authorization: token,
        },
      });
    } catch (error) {
      console.error('🚀 ~ file: loyalty.ts ~ acceptOffer ~ error', error);
      useSentry().captureException(error);
    }
  }

  async function getAdvantages (): Promise<void> {
    try {
      const url = '/v1/content/advantages';
      const {
        data,
      } = await useLoyaltyApi().get<Array<Advantage>>(url);

      advantages.value = data;
    } catch (error) {
      console.error('🚀 ~ file: loyalty.ts ~ getAdvantages ~ error', error);
      useSentry().captureException(error);
    }
  }

  async function getOperationHistory (): Promise<void> {
    try {
      const url = 'v1/agent/operation_history';
      const token = getUserToken();
      const { data } = await useLoyaltyApi().get<Array<PointsHistoryResponse>>(url, {
        headers: {
          Authorization: token,
        },
      });
      pointsHistory.value = data;
    } catch (error) {
      console.error('🚀 ~ file: loyalty.ts ~ getOperationHistory ~ error', error);
      useSentry().captureException(error);
    }
  }

  const pendingConditionsSlugs = new Set<string>();

  async function getConditions (slug: string): Promise<void> {
    if (conditions.value?.[slug] || pendingConditionsSlugs.has(slug)) {
      return;
    }

    pendingConditionsSlugs.add(slug);
    conditionsLoading.value = true;
    conditionsError.value = false;

    try {
      const url = '/v1/content/conditions';
      const token = getUserToken();
      const {
        data,
      } = await useLoyaltyApi().get<Array<ConditionItem>>(url, {
        headers: {
          Authorization: token,
        },
        params: {
          city: slug,
        },
      });

      conditions.value = {
        ...conditions.value,
        [slug]: data,
      };
    } catch (error) {
      console.error('🚀 ~ file: loyalty.ts ~ getConditions ~ error', error);
      useSentry().captureException(error);
      conditionsError.value = true;

      try {
        useNotify().create({
          type: 'negative',
          message: 'Не удалось загрузить условия участия',
        });
      } catch {}
    } finally {
      pendingConditionsSlugs.delete(slug);
      conditionsLoading.value = pendingConditionsSlugs.size > 0;
    }
  }

  async function getRewardCard (payload: RewardRequest): Promise<void> {
    rewardCards.value = undefined; // FIXME отрефакторить strana_lk-4294
    try {
      const url = '/v1/agent/reward_status_card';
      const token = getUserToken();
      const { data } = await useLoyaltyApi().get<Array<RewardResponse>>(url, {
        headers: {
          Authorization: token,
        },
        params: {
          booking_id: payload.id,
          booking_status: payload.status,
          property_type_slug: payload.propertyTypeSlug,
          project_id: payload.projectId,
        },
      });
      rewardCards.value = data;
    } catch (error) {
      console.error('🚀 ~ file: loyalty.ts ~ getRewardCard ~ error', error);
      useSentry().captureException(error);
    }
  }

  async function acceptReward (
    payload: AcceptRewardRequest,
  ): Promise<void> {
    const url = 'v1/agent/accept_reward';
    const token = getUserToken();

    await useLoyaltyApi().post(url, payload, {
      headers: {
        Authorization: token,
      },
    });
  }

  async function cancelReward (payload: Pick<RewardRequest, 'id' | 'status'>): Promise<void> {
    const url = 'v1/agent/cancel_reward';
    const token = getUserToken();

    const body = {
      booking_id: payload.id,
      booking_status: payload.status,
    };

    await useLoyaltyApi().post(url, body, {
      headers: {
        Authorization: token,
      },
    });
  }

  function resetStoreData (): void {
    agentInfo.value = undefined;
    offer.value = undefined;
    advantages.value = [];
    pointsHistory.value = [];
    conditions.value = {};
    rewardCards.value = [];
  }

  return {
    agentInfo,
    offer,
    advantages,
    pointsHistory,
    conditions,
    conditionsLoading,
    conditionsError,
    rewardCards,
    agent,
    loyaltyStatus,
    loyaltyLevelStatus,
    privileges,
    isLastLevel,
    isShowIcon,
    resetStoreData,
    getAgentInfo,
    getOffer,
    acceptOffer,
    getAdvantages,
    getOperationHistory,
    getConditions,
    getRewardCard,
    acceptReward,
    cancelReward,
  };
});
